import { useCallback, useEffect, useRef, useState } from "react";
import apiClient from "../../core/config/api";
import PhoneVerificationModal from "./PhoneVerificationModal";

// Phone verification gate for Free plan users: before an instant verification
// and before a results download. The server enforces it (403
// "phone_verification_required"); this opens the modal first when the status
// says so, or when the server refuses, and then repeats the action.

export const PHONE_VERIFICATION_REQUIRED = "phone_verification_required";

/** Is this axios error the server's "verify your phone first"? Handles blob
 *  (download) responses, whose JSON body arrives as a Blob. */
export const isPhoneVerificationError = async (err: unknown): Promise<boolean> => {
    const response = (err as { response?: { status?: number; data?: unknown } })?.response;
    if (!response || response.status !== 403) return false;
    let data = response.data as { error?: string } | Blob | undefined;
    if (data instanceof Blob) {
        try {
            data = JSON.parse(await data.text());
        } catch {
            return false;
        }
    }
    return (data as { error?: string } | undefined)?.error === PHONE_VERIFICATION_REQUIRED;
};

export const usePhoneGate = () => {
    // null until the status loads; the server still decides either way.
    const [required, setRequired] = useState<boolean | null>(null);
    const [pending, setPending] = useState<(() => void) | null>(null);
    const requiredRef = useRef<boolean | null>(null);

    useEffect(() => {
        requiredRef.current = required;
    }, [required]);

    useEffect(() => {
        let cancelled = false;
        apiClient
            .get("/account/re/phone/status")
            .then(({ data }) => {
                if (!cancelled) setRequired(!!data?.required);
            })
            .catch(() => {
                if (!cancelled) setRequired(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    /** True (and opens the modal, then runs `action`) when the user must verify first. */
    const requirePhone = useCallback((action: () => void): boolean => {
        if (!requiredRef.current) return false;
        setPending(() => action);
        return true;
    }, []);

    /** For a request's catch: true when the server asked for verification (modal opened). */
    const handlePhoneError = useCallback(async (err: unknown, retry: () => void): Promise<boolean> => {
        if (!(await isPhoneVerificationError(err))) return false;
        setRequired(true);
        setPending(() => retry);
        return true;
    }, []);

    const phoneModal = pending ? (
        <PhoneVerificationModal
            purpose="gate"
            onClose={() => setPending(null)}
            onVerified={() => {
                const action = pending;
                requiredRef.current = false; // before the retry, which checks it
                setRequired(false);
                setPending(null);
                action();
            }}
        />
    ) : null;

    return { phoneRequired: required, requirePhone, handlePhoneError, phoneModal };
};
